import { useEffect, useState } from "react";
import { searchGbpBusiness, saveSiteGbp, disconnectSiteGbp, type SiteSummary, type GbpCandidate } from "../lib/authClient";

interface Props {
  open: boolean;
  site: SiteSummary | null;
  onClose: () => void;
  onChanged: () => void;
}

// Per-site Google Business Profile selection. Unlike GSC/GA (Google OAuth), this uses DataForSEO
// to SEARCH for the business and pin it by CID — no OAuth, since the GBP Review Manager reads
// review data through DataForSEO (read-only). The chosen profile is stored on the saved site and
// drives the GBP Review Manager tab.
export function GbpConnectDialog({ open, site, onClose, onChanged }: Props) {
  const [query, setQuery] = useState("");
  const [candidates, setCandidates] = useState<GbpCandidate[]>([]);
  const [selected, setSelected] = useState<string>(""); // cid
  const [searched, setSearched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false); // save in progress → spinner on the Save button
  const [status, setStatus] = useState<{ tone: "info" | "ok" | "err"; text: string } | null>(null);

  useEffect(() => {
    if (!open || !site) return;
    // Default to the business name entered in the cover section, then any already-connected
    // profile name, then the site label.
    const coverName = (localStorage.getItem("cover_business_name") ?? "").trim();
    setQuery(coverName || site.gbpBusinessName || site.label || "");
    setCandidates([]);
    setSelected(site.gbpCid || "");
    setSearched(false);
    setStatus(null);
  }, [open, site]);

  if (!open || !site) return null;
  const siteId = site.id;

  async function handleSearch() {
    if (!query.trim()) return;
    setBusy(true); setStatus({ tone: "info", text: "Searching…" }); setCandidates([]); setSearched(false);
    try {
      const found = await searchGbpBusiness(query.trim());
      setCandidates(found);
      setSearched(true);
      setStatus(found.length ? null : { tone: "info", text: "No businesses found — try adding the city/state or refining the name." });
    } catch (e) {
      setStatus({ tone: "err", text: e instanceof Error ? e.message : "Search failed." });
    } finally { setBusy(false); }
  }

  async function handleSave() {
    const cand = candidates.find(c => c.cid === selected);
    if (!cand) { setStatus({ tone: "err", text: "Pick a business from the list first." }); return; }
    setBusy(true); setSaving(true);
    try {
      await saveSiteGbp(siteId, cand);
      onChanged();
      onClose();
    } catch (e) {
      setStatus({ tone: "err", text: e instanceof Error ? e.message : "Could not connect the profile." });
    } finally { setBusy(false); setSaving(false); }
  }

  async function handleDisconnect() {
    setBusy(true);
    try {
      await disconnectSiteGbp(siteId);
      setSelected(""); setCandidates([]); setSearched(false);
      setStatus({ tone: "info", text: "Disconnected." });
      onChanged();
    } catch (e) {
      setStatus({ tone: "err", text: e instanceof Error ? e.message : "Could not disconnect." });
    } finally { setBusy(false); }
  }

  const btn: React.CSSProperties = { padding: "8px 18px", border: "none", borderRadius: 0, fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "'Inter','Mulish',sans-serif" };
  // White spinning circle for the Save button while the profile is saving (uses the global
  // `seo-spin` keyframe defined in app.css).
  const saveSpinner: React.CSSProperties = { display: "inline-block", width: 12, height: 12, marginRight: 7, border: "2px solid rgba(255,255,255,.45)", borderTopColor: "#fff", borderRadius: "50%", animation: "seo-spin .6s linear infinite" };
  const field: React.CSSProperties = { boxSizing: "border-box", padding: "9px 11px", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: 13, fontFamily: "'Inter','Mulish',sans-serif", color: "#0f172a", background: "#fff", outline: "none" };
  const statusColor = status?.tone === "ok" ? "#16a34a" : status?.tone === "err" ? "#dc2626" : "#2563eb";

  return (
    <div
      role="dialog" aria-modal="true"
      style={{ position: "fixed", inset: 0, zIndex: 4200, background: "rgba(15,23,42,.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "8vh 16px" }}
      onClick={onClose}
    >
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, maxWidth: 600, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,.35)", padding: "22px 24px", color: "#334155", lineHeight: 1.5 }}>
        <h2 style={{ margin: "0 0 4px", fontSize: 17, color: "#0f172a", fontFamily: "'Oswald','Barlow Condensed','Inter',sans-serif", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.7px" }}>
          GBP Profile
        </h2>
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 12, wordBreak: "break-word" }}>{site.label || site.siteUrl}</div>

        <p style={{ fontSize: 12.5, color: "#475569", margin: "0 0 14px", lineHeight: 1.55 }}>
          Search for this website&rsquo;s Google Business Profile and select it. The chosen profile is what
          the <strong>GBP Review Manager</strong> tab tracks.
        </p>

        {site.gbpConnected && (
          <div style={{ fontSize: 12.5, color: "#166534", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "9px 12px", marginBottom: 14 }}>
            ✓ Connected: <strong>{site.gbpBusinessName || "selected profile"}</strong>
          </div>
        )}

        <label style={{ display: "block", fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: "#64748b", marginBottom: 6 }}>
          Enter the full business name or business website URL below
        </label>
        <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Business name or website URL" style={{ ...field, flex: "1 1 200px" }}
            onKeyDown={e => { if (e.key === "Enter") void handleSearch(); }} disabled={busy} />
          <button style={{ ...btn, background: "#2563eb", color: "#fff", opacity: busy || !query.trim() ? 0.6 : 1 }} onClick={() => void handleSearch()} disabled={busy || !query.trim()}>Search</button>
        </div>

        {candidates.length > 0 && (
          <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 8, marginBottom: 6 }}>
            {candidates.map(c => {
              const on = c.cid === selected;
              return (
                <button key={c.cid} onClick={() => setSelected(c.cid)}
                  style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 12px", background: on ? "#eff6ff" : "#fff", border: "none", borderBottom: "1px solid #f1f5f9", cursor: "pointer", fontFamily: "'Inter','Mulish',sans-serif" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: on ? "#1d4ed8" : "#0f172a" }}>{c.name || "(unnamed)"}</div>
                  <div style={{ fontSize: 11.5, color: "#64748b" }}>{c.address || "—"}</div>
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                    {c.rating != null ? `★ ${c.rating}` : "no rating"}{c.reviewCount != null ? ` · ${c.reviewCount.toLocaleString()} reviews` : ""}
                  </div>
                </button>
              );
            })}
          </div>
        )}
        {searched && candidates.length === 0 && !status && (
          <div style={{ fontSize: 12.5, color: "#94a3b8", marginBottom: 6 }}>No results.</div>
        )}

        {status && <div style={{ fontSize: 12.5, color: statusColor, margin: "6px 0 0" }}>{status.text}</div>}

        <div style={{ display: "flex", gap: 10, justifyContent: "space-between", alignItems: "center", marginTop: 18 }}>
          <div>
            {site.gbpConnected && (
              <button style={{ ...btn, background: "#fff", color: "#dc2626", border: "1px solid #fecaca" }} onClick={() => void handleDisconnect()} disabled={busy}>Disconnect</button>
            )}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button style={{ ...btn, background: "#e2e8f0", color: "#334155" }} onClick={onClose} disabled={busy}>Cancel</button>
            <button style={{ ...btn, background: "#16a34a", color: "#fff", opacity: (busy || !selected) ? 0.6 : 1, display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 78 }} onClick={() => void handleSave()} disabled={busy || !selected}>
              {saving ? <><span style={saveSpinner} />Saving…</> : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
